import { styled } from '@linaria/react';
import { Divider, Group, Slider as MantineSlider, Switch as MantineSwitch, Stack } from '@mantine/core';
import { baseWhite, inkLight, secondaryBase } from '../../styles/design-tokens';
import { Icon } from '../../ui-library/icon/icon';
import { Tooltip } from '../../ui-library/tooltip/tooltip';
import { Text } from '../../ui-library/typography/typography';

type FilterGroupToggleSlider = {
  id: string;
  name: string;
  tooltip?: string;
  icon?: string;
  toggleValue: boolean;
  onToggleChange: (value: boolean) => void;
  sliderValue: number;
  onSliderChange: (value: number) => void;
  sliderMin: number;
  sliderMax: number;
  sliderStep?: number;
  sliderLabel?: (value: number) => string;
};

type ToggleSliderProps = {
  toggle: FilterGroupToggleSlider;
  hasDivider?: boolean;
};

export const FilterPanelToggleSlider = ({ toggle, hasDivider = true }: ToggleSliderProps) => {
  return (
    <Stack key={toggle.id} gap={16}>
      <Group justify="space-between">
        <Group gap="xs">
          {toggle.icon && <Icon name={toggle.icon} size={20} color={inkLight} />}
          <Text weight="medium" size="small">
            {toggle.name}
          </Text>
          {toggle.tooltip && (
            <Tooltip label={toggle.tooltip} maw={300} multiline variant="black">
              <Icon name="info" size={16} color={secondaryBase} />
            </Tooltip>
          )}
        </Group>
        <Switch
          aria-label={toggle.name}
          checked={toggle.toggleValue}
          onChange={() => toggle.onToggleChange(!toggle.toggleValue)}
          color={secondaryBase}
        />
      </Group>

      {toggle.toggleValue && (
        <SliderContainer>
          <Slider
            value={toggle.sliderValue}
            onChange={toggle.onSliderChange}
            min={toggle.sliderMin}
            max={toggle.sliderMax}
            step={toggle.sliderStep || 1}
            label={toggle.sliderLabel}
          />
          {toggle.sliderLabel && (
            <Text size="small" color={inkLight} style={{ textAlign: 'center', marginTop: '8px' }}>
              {toggle.sliderLabel(toggle.sliderValue)}
            </Text>
          )}
        </SliderContainer>
      )}
      {hasDivider && <Divider style={{ marginTop: '8px' }} />}
    </Stack>
  );
};

const Switch = styled(MantineSwitch)`
  .mantine-Switch-track {
    height: 25px;
    width: 45px;
    padding: 2px 8px;
    border-radius: 100px;
    border: 2px solid rgba(29, 27, 32, 0.12);
    background: rgba(231, 224, 236, 0.12);
    cursor: pointer;
    box-sizing: border-box;
  }

  .mantine-Switch-thumb {
    height: 14px;
    width: 14px;
    border-radius: 24px;
    margin-left: 2px;
    opacity: 0.38;
    background: #1d1b20;
  }

  input {
    &:checked + .mantine-Switch-track {
      border-color: ${secondaryBase};
      background: ${secondaryBase};

      .mantine-Switch-thumb {
        height: 16px;
        width: 16px;
        background: white;
        z-index: 1;
        left: 22px;
        opacity: 1;
      }
    }
  }
`;

const Slider = styled(MantineSlider)`
  .mantine-Slider-track {
    height: 4px;
  }

  .mantine-Slider-bar {
    background-color: ${secondaryBase};
  }

  .mantine-Slider-thumb {
    width: 20px;
    height: 20px;
    background-color: ${secondaryBase};
    border: 2px solid white;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  .mantine-Slider-label {
    font-size: 12px;
    color: ${baseWhite};
  }
`;

const SliderContainer = styled.div`
  padding: 8px 0;
  padding-left: 10px;
  width: 95%;
  align-items: center;
  justify-content: center;
`;
